import type { AttendanceStatus, KioskClassWeekCell } from "../types/kioskApiTypes";

export const STATUS_BUTTONS: { key: AttendanceStatus; label: string; title: string; activeTone: string }[] = [
  { key: "present",          label: "✓",   title: "anwesend",   activeTone: "bg-emerald-500 text-white" },
  { key: "late",             label: "🕐",  title: "verspätet",  activeTone: "bg-amber-400 text-amber-950" },
  { key: "absent_excused",   label: "Inf", title: "informiert", activeTone: "bg-sky-500 text-white" },
  { key: "absent_unexcused", label: "U",   title: "ungeklärt",  activeTone: "bg-rose-500 text-white" },
];

export interface AbsenceRun {
  student_id: string;
  name: string;
  status: AttendanceStatus;
  notes: string | null;
  start_at: string;
  end_at: string;
  matched_lessons: number;
  total_lessons: number;
}

function normalizeStatus(status: string): AttendanceStatus | null {
  if (status === "late") return "late";
  if (status === "absent_excused" || status === "excused") return "absent_excused";
  // Collapse both `absent_unexcused` (legacy) and `absent_unclear`
  // (now the canonical write-side value, per live data 2026-05-20)
  // into the kiosk's single U bucket. Without this branch
  // absent_unclear returns null and ungeklärt students drop out of
  // the U row + weekly absence-runs summary, even though they
  // still appear in the popup.
  if (
    status === "absent_unexcused" ||
    status === "unexcused" ||
    status === "absent_unclear" ||
    status === "unclear"
  ) return "absent_unexcused";
  if (status === "beurlaubung") return "beurlaubung" as AttendanceStatus;
  return null;
}

export function shortName(full: string): string {
  // "Raghad Al Fandi" → "Raghad A.F.", "Anna Martínez" → "Anna M."
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const rest = parts.slice(1).map((p) => p.charAt(0).toUpperCase() + ".").join("");
  return `${first} ${rest}`;
}

export function fmtHM(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export function computeAbsenceRunsByDay(cells: KioskClassWeekCell[]): Map<number, AbsenceRun[]> {
  const byDay = new Map<number, KioskClassWeekCell[]>();
  for (const c of cells) {
    const dow = Math.min(6, Math.max(1, c.day_of_week));
    (byDay.get(dow) ?? byDay.set(dow, []).get(dow)!).push(c);
  }
  const out = new Map<number, AbsenceRun[]>();
  for (const [dow, dayCells] of byDay) {
    dayCells.sort((a, b) => a.start_at.localeCompare(b.start_at));
    const mergedByStudent = new Map<string, AbsenceRun>();
    const totalLessons = dayCells.length;
    for (const cell of dayCells) {
      // For confirmed cells use the actual saved absentees. For unconfirmed
      // cells fall back to the carry-forward seed so a kid known absent in
      // an earlier lesson doesn't drop out of the summary just because the
      // next period hasn't been Bestätigt yet.
      const source = cell.attendance_recorded
        ? (cell.absentees ?? [])
        : (cell.seed_absentees ?? []);

      const seenInCell = new Set<string>();
      for (const a of source) {
        const normalized = normalizeStatus(a.status);
        if (!normalized || normalized === "late") continue; // late stays in the cell itself
        const key = `${a.student_id}|${normalized}`;
        if (seenInCell.has(key)) continue;
        seenInCell.add(key);

        const existing = mergedByStudent.get(key);
        if (existing) {
          if (cell.start_at < existing.start_at) existing.start_at = cell.start_at;
          existing.end_at = cell.end_at;
          if (!existing.notes && a.notes) existing.notes = a.notes;
          existing.matched_lessons += 1;
        } else {
          mergedByStudent.set(key, {
            student_id: a.student_id,
            name: a.name,
            status: normalized,
            notes: a.notes,
            start_at: cell.start_at,
            end_at: cell.end_at,
            matched_lessons: 1,
            total_lessons: totalLessons,
          });
        }
      }
    }
    const mergedRuns = [...mergedByStudent.values()].sort((a, b) =>
      a.start_at.localeCompare(b.start_at),
    );
    out.set(dow, mergedRuns);
  }
  return out;
}

export const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa"];
export const PERIOD_ROWS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export function fmtWeekRange(weekStartIso: string): string {
  // Mon-anchored week → label as Mo–Fr.
  const start = new Date(weekStartIso + "T00:00:00");
  const end = new Date(start);
  end.setDate(end.getDate() + 4);
  const f = (d: Date) =>
    d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return `${f(start)} – ${f(end)}.${start.getFullYear()}`;
}

export function periodForCell(c: KioskClassWeekCell): number {
  if (c.period_number != null && c.period_number >= 1 && c.period_number <= 9) {
    return c.period_number;
  }
  const start = new Date(c.start_at);
  const minutesFromEight = (start.getHours() - 8) * 60 + start.getMinutes();
  return Math.max(1, Math.min(9, Math.floor(minutesFromEight / 45) + 1));
}
